import { useGetFaireAcademyPageState } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { AcademyGuideCardSmall } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/components/GuideCards/AcademyGuideCardSmall";
import { useGetOtherGuides } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/useGetNextOrOtherGuides";
import { GUIDE_CARDS } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/const";
import { GuideColumnSection } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/styles";
import {
  FaireAcademyGuide,
  OWF_GUIDE_SOURCE_NAME,
} from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { Button } from "@faire/web--source/slate/Button";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackOpenWithFaireGuidesLandingPageCtaClick } from "@faire/web-api--source/events/openWithFaire/click/guidesLandingPageCta";
import { trackOpenWithFaireIndividualGuideCtaClick } from "@faire/web-api--source/events/openWithFaire/click/individualGuideCta";
import openWithFaireAcademy from "@faire/web-api--source/routes/www/open-with-faire/academy";
import * as React from "react";

export const ExploreOtherGuides: React.FC<{
  currentGuide: FaireAcademyGuide;
  isLastChapter: boolean;
}> = ({ currentGuide, isLastChapter }) => {
  const { isMobile } = useViewport();

  const otherGuides = useGetOtherGuides(currentGuide, isLastChapter);
  if (!otherGuides[0]) {
    return null;
  }
  return (
    <>
      <Spacer height={{ mobileAndAbove: "3x", tabletAndAbove: "8x" }} />
      <GuideColumnSection gap="4x">
        {isMobile ? (
          <MobileExploreOtherGuides
            currentGuide={currentGuide}
            otherGuide={otherGuides[0]}
          />
        ) : (
          <DesktopExploreOtherGuides
            currentGuide={currentGuide}
            otherGuides={otherGuides}
          />
        )}
      </GuideColumnSection>
      <Spacer height={{ mobileAndAbove: "5x", tabletAndAbove: "10x" }} />
    </>
  );
};

const DesktopExploreOtherGuides: React.FC<{
  currentGuide: FaireAcademyGuide;
  otherGuides: FaireAcademyGuide[];
}> = ({ currentGuide, otherGuides }) => {
  const { isTablet } = useViewport();
  const { pageState } = useGetFaireAcademyPageState();

  return (
    <>
      <Row justify="space-between" align="flex-end">
        <Title />
        <SeeAllGuidesButton />
      </Row>
      <Row justify="space-between" gap="2x">
        {otherGuides.map((guide) => (
          <AcademyGuideCardSmall
            guide={guide}
            fullWidth
            key={guide.title}
            useSmallHeight={isTablet}
            onClick={() =>
              trackOpenWithFaireIndividualGuideCtaClick(
                pageState,
                currentGuide.title,
                OWF_GUIDE_SOURCE_NAME.INDIVIDUAL_GUIDE_PAGE,
                guide.title
              )
            }
          />
        ))}
      </Row>
    </>
  );
};

const MobileExploreOtherGuides: React.FC<{
  currentGuide: FaireAcademyGuide;
  otherGuide: FaireAcademyGuide;
}> = ({ currentGuide, otherGuide }) => {
  const { pageState } = useGetFaireAcademyPageState();

  return (
    <>
      <Title />
      <AcademyGuideCardSmall
        fullWidth
        guide={otherGuide}
        onClick={() =>
          trackOpenWithFaireIndividualGuideCtaClick(
            pageState,
            currentGuide.title,
            OWF_GUIDE_SOURCE_NAME.INDIVIDUAL_GUIDE_PAGE,
            otherGuide.title
          )
        }
      />
      <SeeAllGuidesButton isMobile />
    </>
  );
};

const Title: React.FC = () => {
  return (
    <Typography variant="displaySSerifRegular">
      <StrictLocalMsg
        defaultMessage="Explore other guides"
        description={{
          text: "Title for section prompting retailer to explore more Faire Academy guides",
          img: "https://cdn.faire.com/fastly/884b8d500c1fb08f0692b5a18b8407e262a754018096c0397bd5897a16864e54.png",
        }}
      />
    </Typography>
  );
};

const SeeAllGuidesButton: React.FC<{ isMobile?: boolean }> = ({
  isMobile = false,
}) => {
  const { pageState } = useGetFaireAcademyPageState();

  return (
    <Button
      variant={isMobile ? "secondary" : "tertiary"}
      linkTo={openWithFaireAcademy()}
      onClick={() =>
        trackOpenWithFaireGuidesLandingPageCtaClick(
          pageState,
          OWF_GUIDE_SOURCE_NAME.INDIVIDUAL_GUIDE_PAGE
        )
      }
    >
      <StrictLocalMsg
        defaultMessage="See all {guidesNumber} guides"
        description={{
          text: "Cta to view all Faire Academy guides. The guidesNumber will always be greater than 1",
          img: "https://cdn.faire.com/fastly/884b8d500c1fb08f0692b5a18b8407e262a754018096c0397bd5897a16864e54.png",
        }}
        values={{ guidesNumber: GUIDE_CARDS.length }}
      />
    </Button>
  );
};
